import { useEffect, useState } from "react";
import { TrafficCone, Loader2, CheckCircle2, XCircle, RefreshCw, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { getSemaforoStats, getSemaforoLeads, resendSemaforoLead, semaforoPdfUrl, liveGet, livePost, livePdfUrl } from "@/lib/api";
import { useLive, LiveBadge } from "./useLive";

const COL = { rosso: "#EF4444", giallo: "#F59E0B", verde: "#34D399" };
const SIT = { due_senza_figli: "2 stip. senza figli", due_con_figli: "2 stip. con figli", monoreddito: "monoreddito", numerosa: "numerosa" };
const OSS = { ossigeno_meno_1m: "< 1 mese", ossigeno_1_3m: "1-3 mesi", ossigeno_3m_piu: "3+ mesi" };
const ENT = { meno_1500: "< 1.500 €", "1500_2500": "1.500-2.500", "2500_3500": "2.500-3.500", oltre_3500: "> 3.500 €" };
const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const publicUrl = () => `${window.location.origin}/semaforo`;

const Dist = ({ title, data, pct, labels, colors }) => (
  <div className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-3" data-testid={`semaforo-dist-${title}`}>
    <p className="text-[0.65rem] uppercase tracking-wide text-neutral-500 mb-2">{title}</p>
    <div className="space-y-1.5">
      {Object.keys(data).map((k) => (
        <div key={k} className="flex items-center gap-2 text-xs">
          <span className="w-28 shrink-0 text-neutral-400 truncate">{labels?.[k] || k}</span>
          <div className="flex-1 h-2 rounded bg-[#1a1a1a] overflow-hidden"><div className="h-full" style={{ width: `${pct[k] || 0}%`, background: colors?.[k] || "#D97706" }} /></div>
          <span className="w-14 text-right text-neutral-300">{data[k]} · {pct[k] || 0}%</span>
        </div>
      ))}
    </div>
  </div>
);

export function SemaforoPanel() {
  const live = useLive();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!live) return;
    const fetchStats = live.live ? () => liveGet("semaforo/stats") : getSemaforoStats;
    const fetchLeads = live.live ? () => liveGet("semaforo/leads") : getSemaforoLeads;
    setLoading(true);
    Promise.all([fetchStats().catch(() => null), fetchLeads().catch(() => [])])
      .then(([s, l]) => { setStats(s); setLeads(l); }).finally(() => setLoading(false));
  }, [live]);

  const pdfUrl = (id) => (live?.live ? livePdfUrl(live.base_url, id) : semaforoPdfUrl(id));
  const resend = async (id) => {
    setBusy(id);
    try {
      const upd = live?.live ? await livePost(`semaforo/leads/${id}/resend`) : await resendSemaforoLead(id);
      setLeads((ls) => ls.map((l) => (l.id === id ? upd : l)));
      upd.forwarded ? toast.success("Inviato a GHL") : toast.error("GHL non configurato o non risponde");
    } catch { toast.error("Errore nel reinvio"); }
    setBusy(null);
  };

  const f = stats?.funnel || { view: 0, start: 0, complete: 0, lead: 0 };
  const reali = leads.filter((l) => !(l.email || "").toLowerCase().includes("test"));
  const ok = reali.filter((l) => l.forwarded).length;

  return (
    <section className="border border-[#262626] rounded-xl bg-[#111111] p-6 space-y-5" data-testid="strategy-semaforo">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-white text-lg flex items-center gap-2"><TrafficCone size={18} style={{ color: "#F59E0B" }} /> Semaforo Economico Familiare — test, colori, lead</h2>
          <p className="text-xs text-neutral-500 mt-1">Il lead magnet pubblico. Ogni test completato è un dato tuo (l'Osservatorio). I lead arrivano a GHL con i tag colore · situazione · ossigeno.</p>
        </div>
        <div className="flex items-center gap-2">
          <LiveBadge live={live} testid="semaforo-live-badge" />
          <a href={live?.live ? `${live.base_url}/semaforo` : publicUrl()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#3A4763] text-neutral-200 hover:border-[#F59E0B]" data-testid="semaforo-open-public"><ExternalLink size={13} /> Apri /semaforo</a>
        </div>
      </div>

      {loading || !live ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 size={14} className="animate-spin" /> Carico…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="semaforo-funnel">
            {[["Visite", f.view, "semaforo-tot-view"], ["Iniziati", f.start, "semaforo-tot-start"], ["Completati", f.complete, "semaforo-tot-complete"], ["Lead", f.lead, "semaforo-tot-lead"],
              ["Lead / test", stats?.conv_lead == null ? "–" : `${stats.conv_lead}%`, "semaforo-tot-conv"]].map(([l, v, tid]) => (
              <div key={l} className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-3" data-testid={tid}>
                <p className="text-[0.65rem] uppercase tracking-wide text-neutral-500">{l}</p>
                <p className="font-display font-bold text-2xl mt-1 text-white">{v}</p>
              </div>
            ))}
          </div>
          {stats && stats.test_totali > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Dist title="colori" data={stats.colori} pct={stats.colori_pct} colors={COL} />
              <Dist title="situazione" data={stats.situazioni} pct={stats.situazioni_pct} labels={SIT} />
              <Dist title="ossigeno" data={stats.ossigeno} pct={stats.ossigeno_pct} labels={OSS} />
              <Dist title="entrate" data={stats.entrate || {}} pct={stats.entrate_pct || {}} labels={ENT} />
            </div>
          )}
          <div className="space-y-2" data-testid="semaforo-leads">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-neutral-500">Lead Semaforo · {reali.length}</p>
              <p className="text-xs" data-testid="semaforo-leads-ghl"><span style={{ color: ok === reali.length ? "#16A34A" : "#D92D20" }}>{ok}/{reali.length}</span><span className="text-neutral-500"> arrivati a GHL</span></p>
            </div>
            {reali.length === 0 ? (
              <p className="text-sm text-neutral-500" data-testid="semaforo-leads-empty">Nessun lead reale ancora.</p>
            ) : (
              <ul className="divide-y divide-[#1a1a1a] max-h-72 overflow-y-auto" data-testid="semaforo-leads-list">
                {reali.map((l) => (
                  <li key={l.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`semaforo-lead-${l.id}`}>
                    <span className="text-neutral-500 text-xs w-24 shrink-0">{fmt(l.created_at)}</span>
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: COL[l.colore] }} title={l.colore} />
                    <span className="text-neutral-200 truncate flex-1">{l.nome}</span>
                    <span className="text-xs text-neutral-500 hidden sm:inline w-28 truncate">{SIT[l.situazione]} · {l.mesi_label}</span>
                    <a href={pdfUrl(l.id)} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-white" title="PDF" data-testid={`semaforo-pdf-${l.id}`}><FileText size={15} /></a>
                    {l.forwarded ? (
                      <CheckCircle2 size={16} className="shrink-0" style={{ color: "#16A34A" }} />
                    ) : (
                      <button onClick={() => resend(l.id)} disabled={busy === l.id} className="flex items-center gap-1 text-xs shrink-0" style={{ color: "#D92D20" }} data-testid={`semaforo-resend-${l.id}`}>
                        <XCircle size={16} /> <RefreshCw size={12} className={busy === l.id ? "animate-spin" : ""} /> reinvia
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}
